import React from "react";

import AlignLeftIcon from "@uui/AlignLeft01";
import LayoutLeftIcon from "@uui/LayoutLeft";
import CloseIcon from "@uui/XClose";
import { every } from "lodash/fp";

import HeaderButton from "@app/analytics/components/SideNav/HeaderButton";
import useRecommendations from "@app/analytics/hooks/recommendations/useRecommendations";
import useWorkspaceInvite from "@app/analytics/hooks/workspace/useWorkspaceInvite";
import { Textmark } from "@app/common/assets/icons";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import Config from "@app/config";

import LogomarkWithGradient from "@rui/LogomarkWithGradient";
import { Row } from "@rui/Stack";

import { ButtonConfig, SIDE_NAV_BUTTON_GROUPS } from "./constants";
import CreateWorkspaceLink from "./CreateWorkspaceLink";
import SideNavButton from "./SideNavButton";
import {
  SideNavContentContainer,
  SideNavDivider,
  SideNavLogoContainer,
  SideNavToggleButton,
} from "./styles";
import WorkspaceInviteLink from "./WorkspaceInviteLink";
import LocaleSwitcher from "../LocaleSwitcher";
import PlanLink from "../PlanLink";

interface SideNavContentProps {
  expanded?: boolean;
  open: boolean;
  onClickMenuItem?: () => void;
  onClose?: () => void;
  onToggleExpanded?: () => void;
  sideNavModalVisible: boolean;
}

const SideNavContent = ({
  expanded = false,
  open,
  sideNavModalVisible,
  onClose,
  onClickMenuItem,
  onToggleExpanded,
}: SideNavContentProps) => {
  const { inDesktopView } = useResponsiveView();
  const featureAccess = useFeatureAccess();
  const { pendingInvite } = useWorkspaceInvite();
  const { workspaceId, inWorkspace } = useWorkspace();
  const { groupedPendingRecommendations } = useRecommendations();

  const routeParams = { workspaceId: workspaceId ?? "" };

  const notifications: { [key: string]: number } = {
    Recommendations: groupedPendingRecommendations.length,
  };

  const getNotifications = (buttonConfig: ButtonConfig) => {
    if (
      !buttonConfig.allowNotifications ||
      !notifications[buttonConfig.label]
    ) {
      return 0;
    }

    return notifications[buttonConfig.label];
  };

  const showLocaleSwitcher =
    Config.flags.enableSpanishLocale && inDesktopView && open;
  const shouldHideNonOptimizedMobileRoutes =
    Config.flags.enableMobileNav && !inDesktopView;

  return (
    <>
      {/* header button is outside the scrollable div so that it stays fixed on the top */}
      <SideNavLogoContainer
        variant="apart"
        minWidth={open ? "200px" : 0}
        gap="8px"
      >
        <HeaderButton url={ROUTES.LANDING_PAGE} sx={{ flexGrow: 1 }}>
          {open ? (
            <Row variant="left" gap="8px">
              <LogomarkWithGradient />
              <Textmark sx={{ width: "74px" }} />
            </Row>
          ) : (
            <LogomarkWithGradient />
          )}
        </HeaderButton>
        {showLocaleSwitcher && <LocaleSwitcher onClose={onClose} />}
        {onToggleExpanded && open ? (
          <SideNavToggleButton onClick={onToggleExpanded}>
            {expanded ? <AlignLeftIcon /> : <LayoutLeftIcon />}
          </SideNavToggleButton>
        ) : (
          <SideNavToggleButton onClick={onClose}>
            <CloseIcon />
          </SideNavToggleButton>
        )}
      </SideNavLogoContainer>
      <SideNavContentContainer spacing={0.5}>
        <SideNavDivider sx={{ mt: 0, mb: 1.5 }} />
        <PlanLink
          onClick={onClickMenuItem}
          canEditBilling={featureAccess.canEditBillingPlan}
          expanded={open}
          highlighted={sideNavModalVisible}
        />
        <SideNavDivider />
        {pendingInvite ? (
          <>
            <WorkspaceInviteLink expanded={open} />
            <SideNavDivider />
          </>
        ) : inWorkspace ? null : (
          <>
            <CreateWorkspaceLink expanded={open} />
            <SideNavDivider />
          </>
        )}
        {SIDE_NAV_BUTTON_GROUPS.map(({ key, items }) => {
          if (
            shouldHideNonOptimizedMobileRoutes &&
            every((item) => !item.mobileOptimized, items)
          ) {
            return null;
          }

          return (
            <React.Fragment key={key}>
              {items.map((buttonConfig) => {
                if (
                  !buttonConfig.enabled(featureAccess) ||
                  (shouldHideNonOptimizedMobileRoutes &&
                    !buttonConfig.mobileOptimized)
                ) {
                  return null;
                }

                return (
                  <SideNavButton
                    {...buttonConfig}
                    key={buttonConfig.label}
                    Icon={buttonConfig.icon}
                    open={open}
                    url={buttonConfig.url(routeParams)}
                    notifications={getNotifications(buttonConfig)}
                    onClick={onClickMenuItem}
                  />
                );
              })}
              <SideNavDivider />
            </React.Fragment>
          );
        })}
      </SideNavContentContainer>
    </>
  );
};

export default SideNavContent;
